import { useState } from 'react';
import { SelectOption } from '../searchableSelect/searchableSelectTypes';
import { getTranslationFromTranslatableEntity } from '../../constants/matter';
import { useLanguage } from '../../context/LanguageContext';
import { useTask } from '../../context/TaskContext';
import { useFetchActivityStatus } from '../../hooks/useFetchActivityStatus';
import { ITask, ITaskStatus } from '../../interface/tasks';

const STATUS_PAGE_SIZE = 50;

/** The props `SearchableSelect` needs to pick a task status, from the same list the picker screen uses. */
export const useTaskStatusSelect = () => {
  const { task, updateTask } = useTask();
  const { getAppLanguageCode } = useLanguage();
  const [searchValue, setSearchValue] = useState('');
  const {
    data: statuses,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useFetchActivityStatus({ pageSize: STATUS_PAGE_SIZE, search: searchValue });
  const appLanguage = getAppLanguageCode();

  const toOption = (status: ITaskStatus): SelectOption => ({
    value: status.id,
    label: getTranslationFromTranslatableEntity(status.translations, appLanguage),
  });

  const options: SelectOption[] = statuses.map(toOption);
  const value: SelectOption | null = task?.status?.id ? toOption(task.status) : null;

  const onChange = (option: SelectOption) => {
    const status = statuses.find(({ id }) => id === option.value);
    if (!status) return;
    updateTask({ ...task, statusId: status.id, status } as ITask);
  };

  return {
    options,
    value,
    onChange,
    onSearch: setSearchValue,
    onLoadMore: fetchNextPage,
    hasMore: Boolean(hasNextPage),
    loading: isLoading,
    loadingMore: isFetchingNextPage,
  };
};
